import React from "react";
import { useState, useEffect } from "react";

import { useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import axios from "axios";
import { KeyboardEvent } from "react";

import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { Helmet } from "react-helmet";

import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

import Footer from "../PageComponents/Footer";
import Toolbar from "@mui/material/Toolbar";

import { useNavigate } from "react-router-dom";
import Link from "@mui/material/Link";

import PagePaper from "../PageComponents/PagePaper";
import Page from "../PageComponents/Page";

import { useLocation } from "react-router-dom";

import GetCSRFToken from "./GetCSRFToken";

function VerifyEmailPage() {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();

  const location = useLocation();

  // TODO: only prompt for the code input if the user is logged in (else display a message saying to check their email for a link? we could also prompt them to log in again?)

  const [code, setCode] = useState("");
  const [wrongCodeCount, setWrongCodeCount] = useState(0); // TODO: after 2 or 3 do something else?
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [generalError, setGeneralError] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const RedirectSecondsConst = 5; // TODO: make this a drop-in component
  const [redirectSeconds, setRedirectSeconds] = useState(RedirectSecondsConst);
  const [redirectTimeout, setRedirectTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const [countDownInterval, setCountDownInterval] = useState<NodeJS.Timeout | undefined>(undefined);

  function cleanUp() {
    if (countDownInterval) {
      clearInterval(countDownInterval);
    }
    setCountDownInterval(undefined);
    setRedirectSeconds(RedirectSecondsConst);
  }

  function handleSuccess() {
    setSuccess(true);
    setLoading(false);
    setCountDownInterval(
      setInterval(() => {
        setRedirectSeconds((s) => Math.max(0, s - 1));
      }, 1000)
    );
    setRedirectTimeout(
      setTimeout(() => {
        cleanUp();
        navigate("/"); // TODO: make this a /login route to prompt a login?
      }, RedirectSecondsConst * 1000)
    );
  }

  function handleSubmit(mode: "code" | "token") {
    const postParams = mode == "code" ? { code: code } : { uidb64: uidb64, token: token };
    setLoading(true);
    axios
      .post("/api/user/verify-email/complete", postParams, {
        headers: { "X-CSRFToken": GetCSRFToken() },
      })
      .then((res) => handleSuccess())
      .catch((err) => {
        setCode("");
        [
          {
            key: "code",
            fn: setCodeError,
          },
          {
            key: "general",
            fn: setGeneralError,
          },
        ].map((error_details_handlers) => error_details_handlers.fn(err.response.data[error_details_handlers.key]));
        setLoading(false);
      });
  }

  useEffect(() => {
    if (uidb64 !== undefined && token !== undefined) {
      handleSubmit("token");
    } else {
      axios
        .get("/api/user/email-verified", {
          headers: { "X-CSRFToken": GetCSRFToken() },
        })
        .then((res) => {
          handleSuccess();
        })
        .catch((err) => {
          // TODO: handle this more gracefully
        });
    }
  }, [uidb64, token]);

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (e.key === "Enter") {
      handleSubmit("code");
    }
  }

  return (
    <Page pageLoading={false}>
      <Helmet>
        <title>Salus | Verify Email</title>
      </Helmet>
      <PagePaper title={"Verify Email"}>
        {generalError === undefined ? (
          success ? (
            <Typography color="main" variant="body1">
              Email verified! Redirecting in {redirectSeconds} second{redirectSeconds === 1 ? "" : "s"}...or click{" "}
              <Link
                onClick={() => {
                  if (redirectTimeout) {
                    clearTimeout(redirectTimeout);
                  }
                  cleanUp();
                  navigate("/profile");
                }}
              >
                here
              </Link>{" "}
              to be redirected now.
            </Typography>
          ) : (
            <>
              <Typography component="div" variant="body1">
                Please check your email for the code or click the link in your inbox to verify your email.
                <Typography variant="body2" color="text.secondary">
                  If you don't see an email within 2 minutes, please check your spam folder or click below to resend it.
                </Typography>
              </Typography>
              <FormControl
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                  paddingTop: 1,
                }}
              >
                <TextField
                  variant="outlined"
                  error={codeError !== undefined}
                  autoComplete="one-time-code"
                  fullWidth
                  value={code}
                  onChange={(newValue) => {
                    setCodeError(undefined);
                    setCode(newValue.target.value);
                  }}
                  disabled={loading}
                  onKeyDown={catchEnterKeyDownToSubmit}
                  label="Email Verification Code"
                  helperText={codeError}
                />
              </FormControl>
            </>
          )
        ) : (
          <Typography color="error" variant="body1">
            {generalError}
          </Typography>
        )}

        <LoadingButton
          variant="contained"
          size="large"
          color={success ? "success" : undefined}
          endIcon={success ? <CheckIcon /> : <MarkEmailReadOutlinedIcon />}
          loading={loading && !success}
          onClick={() => {
            if (!success) {
              handleSubmit("code");
            }
          }}
          disabled={generalError !== undefined || code === undefined || code === ""}
          sx={{ whiteSpace: "nowrap" }}
        >
          {success ? "Verified!" : "Verify via code"}
        </LoadingButton>
        {success ? null : (
          <Button
            variant="text"
            size="small"
            sx={{ width: "fit-content", alignSelf: "center" }}
            onClick={() =>
              axios
                .get("/api/user/verify-email/start", {
                  headers: { "X-CSRFToken": GetCSRFToken() },
                })
                .then((res) => {})
                .catch((err) => {
                  if (process.env.NODE_ENV === "development") {
                    console.log(err);
                  } // TODO: handle failed verify email start (including already verified, maybe don't even allow this button to be clicked?)
                })
            }
          >
            Resend Verification Email
          </Button>
        )}
      </PagePaper>
      <Button
        color="inherit"
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          if (location.key === "default") {
            navigate("/");
          } else {
            navigate(-1);
          }
        }}
        disabled={loading || success}
      >
        Back to Salus
      </Button>
    </Page>
  );
}

export default VerifyEmailPage;
